import * as React from 'react';
import { matchPath, useLocation, useNavigate, useParams } from 'react-router-dom';

import type { MobileBottomNavItem } from '@/components/ui/MobileBottomNav';
import { ROUTES } from '@/configs/routes';

import { getBottomNavItems, getBottomNavRoute, type OrganizationBottomNavKey } from './config';

type UseOrganizationBottomNavResult = {
  items: MobileBottomNavItem[];
  activeKey: string | null;
  onChange: (key: string) => void;
};

export const useOrganizationBottomNav = (
  enabledSections: OrganizationBottomNavKey[],
  options?: {
    getLabel?: (key: OrganizationBottomNavKey) => string | null;
  },
): UseOrganizationBottomNavResult => {
  const { id } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const getLabel = options?.getLabel;
  const items = getBottomNavItems(enabledSections).map((item) => ({
    ...item,
    label: getLabel?.(item.key as OrganizationBottomNavKey) ?? item.label,
  }));

  const activeKey = React.useMemo(() => {
    if (!id) {
      return null;
    }

    const isDocumentsChat = Boolean(matchPath(ROUTES.organization.documents.chat.path, pathname));

    if (isDocumentsChat) {
      return 'chat';
    }

    const isDocuments = Boolean(matchPath({ path: ROUTES.organization.documents.path, end: false }, pathname));
    const isNews = Boolean(matchPath({ path: ROUTES.organization.news.path, end: false }, pathname));
    const isNotebook = Boolean(matchPath({ path: ROUTES.organization.notebook.path, end: false }, pathname));
    const isMacro = Boolean(matchPath({ path: ROUTES.organization.macro.path, end: false }, pathname));

    if (isNews) {
      return 'news';
    }

    if (isNotebook) {
      return 'notebook';
    }

    if (isMacro) {
      return 'macro';
    }

    if (isDocuments) {
      return 'documents';
    }

    return null;
  }, [id, pathname]);

  const onChange = React.useCallback(
    (key: string) => {
      if (!id) {
        return;
      }

      navigate(getBottomNavRoute(key as OrganizationBottomNavKey, id));
    },
    [id, navigate],
  );

  return { items, activeKey, onChange };
};
